export interface TooltipPoint {
  x: number
  y: number
}

export interface TooltipSize {
  width: number
  height: number
}

export interface TooltipPlacementInput {
  /** Where the pointer is, in viewport coordinates. */
  cursor: TooltipPoint
  /** The tooltip's own measured size. */
  size: TooltipSize
  viewport: TooltipSize
  /** Gap kept between the pointer and the tooltip. */
  offset: number
  /** Gap kept between the tooltip and the viewport edges. */
  margin: number
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max))

/**
 * Viewport position for a tooltip that follows the pointer: below and to the
 * right by default, flipped to the left / above the pointer when that side
 * would run off the viewport, then clamped so it never leaves it. A tooltip
 * larger than the viewport pins to the margin rather than going negative.
 */
export const placeTooltip = ({
  cursor,
  size,
  viewport,
  offset,
  margin,
}: TooltipPlacementInput): { left: number; top: number } => {
  const overflowsRight =
    cursor.x + offset + size.width > viewport.width - margin
  const overflowsBottom =
    cursor.y + offset + size.height > viewport.height - margin

  const left = overflowsRight
    ? cursor.x - offset - size.width
    : cursor.x + offset
  const top = overflowsBottom
    ? cursor.y - offset - size.height
    : cursor.y + offset

  return {
    left: clamp(left, margin, viewport.width - size.width - margin),
    top: clamp(top, margin, viewport.height - size.height - margin),
  }
}
